<template>
  <table-column-picker
    v-slot="{ headers, activatorProps }"
    :headers="incidentListHeaders"
    :storage-key="`incident-table-${route.name}`"
    title="Налаштування відображення переліку інцидентів">
    <b-server-table
      :filter-props="filterProps"
      :headers="headers"
      :request-func="getItems"
      @click:row="openIncident">
      <template #action-button>
        <div class="d-flex align-center ga-3">
          <table-column-picker-button
            v-bind="activatorProps"></table-column-picker-button>
          <v-btn
            v-if="can('incidents.create')"
            color="grey-darken-4"
            @click.stop="createIncident">
            <v-icon class="mr-1">mdi-plus</v-icon>
            Створити інцидент
          </v-btn>
        </div>
      </template>

      <template #item.number="{ item }">
        <span class="font-weight-bold">{{ item.number }}</span>
      </template>

      <template #item.name="{ item }">
        <v-tooltip :text="item.name">
          <template #activator="{ props }">
            <div class="text-truncate" v-bind="props">
              {{ item.name }}
            </div>
          </template>
        </v-tooltip>
      </template>

      <template #item.occurredDate="{ item }">
        {{
          item.occurredDate ? new CustomDate(item.occurredDate).toString() : ''
        }}
      </template>

      <template #item.created_at="{ item }">
        {{ item.created_at ? new CustomDate(item.created_at).toString() : '' }}
      </template>

      <template #item.detectedDate="{ item }">
        {{
          item.detectedDate ? new CustomDate(item.detectedDate).toString() : ''
        }}
      </template>

      <template #item.resolvedDate="{ item }">
        {{
          item.resolvedDate ? new CustomDate(item.resolvedDate).toString() : ''
        }}
      </template>

      <template #item.type="{ item }">
        {{ item.type?.name }}
      </template>

      <template #item.category="{ item }">
        {{ item.category?.name }}
      </template>

      <template #item.status="{ item }">
        <v-chip
          :style="getIncidentStatusChipStyle(item.status?.id)"
          border
          class="font-weight-bold"
          rounded="lg"
          size="small"
          variant="flat">
          {{ item.status?.name }}
        </v-chip>
      </template>

      <template #item.priority="{ item }">
        <div class="d-flex align-center ga-2">
          <v-icon
            :color="getIncidentPriorityColor(item.priority?.id)"
            size="x-small">
            mdi-circle
          </v-icon>
          <span>{{ item.priority?.name }}</span>
        </div>
      </template>

      <template #item.initiator="{ item }">
        {{
          item.initiator
            ? getPersonName(item.initiator, { initials: true })
            : ''
        }}
      </template>

      <template #item.responsible="{ item }">
        {{
          item.responsible || item.initiator
            ? getPersonName(item.responsible || item.initiator, {
                initials: true,
              })
            : ''
        }}
      </template>

      <template #bottom></template>
    </b-server-table>
  </table-column-picker>
</template>

<script lang="ts" setup>
import { useRoute, useRouter } from 'vue-router'
import { BServerTable } from 'best-modules/components'
import TableColumnPicker from '@/components/TableColumnPicker.vue'
import TableColumnPickerButton from '@/components/buttons/TableColumnPickerButton.vue'
import { incidentListHeaders } from '@/utils/tableHeaders'
import { incidentList } from '@/requests/incident'
import { CustomDate, getPersonName, Query } from 'best-modules/utils'
import type { Incident } from '@/utils/types/incident'
import {
  getIncidentPriorityColor,
  getIncidentStatusChipStyle,
} from './constants'
import { useCan } from '@/composables/useCan'

defineOptions({
  name: 'IncidentList',
})

const route = useRoute()
const router = useRouter()
const { can } = useCan()

const filterProps = {
  searchPlaceholder:
    'Пошук за номером, заголовком, описом, підрозділом, відповідальним...',
  scope: 'incident',
  filterMap: [
    { key: 'search' },
    { key: 'typeId', directory: 'incidentTypes', component: 'select' },
    { key: 'categoryId', directory: 'incidentCategories', component: 'select' },
    { key: 'statusId', directory: 'incidentStatuses', component: 'select' },
    { key: 'priorityId', directory: 'incidentPriorities', component: 'select' },
    {
      key: 'initiatorId',
      directory: 'userList',
      component: 'autocomplete',
    },
    {
      key: 'occurredDate',
      dateRangeKeys: ['occurredDateStart', 'occurredDateEnd'],
      component: 'input-date-range',
    },
    {
      key: 'detectedDate',
      dateRangeKeys: ['detectedDateStart', 'detectedDateEnd'],
      component: 'input-date-range',
    },
    {
      key: 'resolvedDate',
      dateRangeKeys: ['resolvedDateStart', 'resolvedDateEnd'],
      component: 'input-date-range',
    },
  ],
}

const getItems = (query: Query) => {
  return incidentList(query)
}

const openIncident = (incident: Incident) => {
  router.push({ name: 'incident-overview', params: { id: incident.id } })
}

const createIncident = () => {
  if (!can('incidents.create')) return

  router.push({ name: 'incident-create' })
}
</script>
